import { SetStateAction, Dispatch, useEffect, useState } from 'react';
import { DevHubAutocomplete, DevHubTextField } from '@devxdiscover/devhub-ui';
import { gql, useQuery } from '@apollo/client';
import { AppRole, RoleId } from '../types/RoleTypes';

const RolesQuery = gql`
  query Roles {
    resources {
      roles {
        id
        name
        displayName
      }
    }
  }
`;

type Props = {
  setSelectedRole: Dispatch<SetStateAction<AppRole>>;
  selectedRole?: AppRole;
};

/**
 * Search for a role
 * @param props props of the component
 * @param props.setSelectedRole setter hook for value selected in form
 */
export function RoleSearch(props: Props) {
  const [roles, setRoles] = useState<AppRole[]>([]);

  const { data, loading, error } = useQuery(RolesQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const filterRoles = (role: AppRole): boolean => {
    // Filter out Owner, Data Lead and Data Business Owner roles (functional area)
    return (
      role.name !== 'owner' &&
      role.id !== RoleId.DataBusinessOwner &&
      role.id !== RoleId.DataLead
    );
  };

  useEffect(() => {
    if (data?.resources?.roles?.length > 0) {
      setRoles(data?.resources?.roles?.filter(filterRoles));
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Event Handler to set selected role from a drop down
  const onSelect = (_: any, value: AppRole | null) => {
    if (!value) {
      return;
    }
    props.setSelectedRole(value);
  };

  return (
    <>
      {error ? (
        <DevHubTextField
          error
          label="Error"
          defaultValue="No Roles Available"
          helperText="Failed to fetch role details."
          variant="outlined"
        />
      ) : (
        <>
          <DevHubAutocomplete
            loading={loading}
            loadingText="Loading.."
            options={roles}
            onChange={onSelect}
            getOptionLabel={(option: AppRole) => option?.displayName}
            defaultValue={props?.selectedRole ? props.selectedRole : undefined}
            renderInput={params => (
              <DevHubTextField
                {...params}
                label="Search for a role"
                margin="normal"
                helperText="Role you wish to modify"
                variant="outlined"
                required
                InputProps={params.InputProps}
              />
            )}
          />
        </>
      )}
    </>
  );
}
